// uxBeacon — 열쇠 없는 1차 수집 (2026-09-13, 인수인계 docs/ops/lay-ux-auto-analysis-handover.md §3-1 · 대표 지시 「모두 자동으로」)
//
// PostHog 가 자동으로 모으던 신호를 우리 CS DB 에도 보낸다. 외부 계정·키·대시보드 설정이 필요 없다.
//   신호: 화면 이동(view: route·prev) · 화면 떠남(leave: seconds) · 분노 클릭(rage: 같은 자리 1초 안 3번) · 죽은 클릭(dead: 500ms 안 DOM·URL 변화 없음)
//        · 오류(error: window.onerror / unhandledrejection 메시지 200자) · 클릭 대상(click: 셀렉터 + 마스킹된 글자 40자)
//   식별: sha256(이메일) 앞 16자(analytics.js distinctIdFor 와 같은 규칙) · 대리 접속 중에는 'anon' · 세션 id 는 탭 단위 난수(sessionStorage)
//   보내기: 20초마다 또는 화면을 떠날 때 navigator.sendBeacon(text/plain — preflight 없음) · 큐 최대 200개 · 실패해도 앱은 모른다
//   끄기: REACT_APP_UX_BEACON_URL 이 없으면 아무것도 하지 않는다(데모·테스트 빌드). 민감 화면(analytics.js SENSITIVE_PATHS)에서는 아무것도 보내지 않는다.
//   입력값은 절대 담지 않는다. 클릭 글자는 숫자·이메일·긴 숫자열을 가리고 40자로 자른다.
import { isSensitivePath, distinctIdFor } from "./analytics";
import { isImpersonating } from "./policyConsentRules";

export const UX_BEACON_URL = process.env.REACT_APP_UX_BEACON_URL || "";
export const QUEUE_MAX = 200;
export const FLUSH_MS = 20000;
const RAGE_WINDOW_MS = 1000;
const RAGE_RADIUS_PX = 30;
const RAGE_COUNT = 3;
const DEAD_WAIT_MS = 500;

export function uxBeaconEnabled() { return Boolean(UX_BEACON_URL); }

/** 클릭 글자 마스킹 — 이메일·긴 숫자열·숫자를 가리고 40자 */
export function maskText(s) {
  return String(s || "")
    .replace(/\s+/g, " ")
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "***@***")
    .replace(/\d[\d\- ]{5,}\d/g, "****")
    .replace(/\d/g, "#")
    .trim()
    .slice(0, 40);
}

/** 요소 셀렉터 — 태그 + id/data-testid/aria-label/첫 class. 120자 */
export function selectorOf(el) {
  if (!el || !el.tagName) return "";
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : "";
  const tid = el.getAttribute?.("data-testid") ? `[data-testid=${el.getAttribute("data-testid")}]` : "";
  const aria = el.getAttribute?.("aria-label") ? `[aria-label=${maskText(el.getAttribute("aria-label")).slice(0, 24)}]` : "";
  const cls = typeof el.className === "string" && el.className ? `.${el.className.trim().split(/\s+/)[0]}` : "";
  return `${tag}${id}${tid}${aria}${cls}`.slice(0, 120);
}

/** 분노 클릭 판정 — 최근 클릭 목록에서 같은 자리(30px) 1초 안 3번이면 true (순수 함수) */
export function isRage(recent, now, x, y) {
  const near = recent.filter((c) => now - c.t <= RAGE_WINDOW_MS && Math.abs(c.x - x) <= RAGE_RADIUS_PX && Math.abs(c.y - y) <= RAGE_RADIUS_PX);
  return near.length + 1 >= RAGE_COUNT;
}

function sessionId() {
  try {
    let s = sessionStorage.getItem("ux_sid");
    if (!s) { s = Math.random().toString(36).slice(2, 12) + Date.now().toString(36); sessionStorage.setItem("ux_sid", s); }
    return s;
  } catch (e) { return "s" + Math.random().toString(36).slice(2, 12); }
}

function surfaceOf() {
  try {
    const p = (typeof window !== "undefined" && window.Capacitor?.getPlatform?.()) || "web";
    return p === "ios" || p === "android" ? p : "web";
  } catch (e) { return "web"; }
}

// ── 큐 ────────────────────────────────────────────────────────────────────────
const state = { queue: [], started: false, hash: "anon", route: null, routeAt: 0, clicks: [], timer: null };

export function __state() { return state; }
export function __reset() { state.queue = []; state.started = false; state.hash = "anon"; state.route = null; state.routeAt = 0; state.clicks = []; if (state.timer) clearInterval(state.timer); state.timer = null; }

export function push(kind, route, payload = {}) {
  if (!uxBeaconEnabled() || !route || isSensitivePath(route)) return false;
  if (state.queue.length >= QUEUE_MAX) state.queue.shift();
  state.queue.push({ kind, route, payload, at: new Date().toISOString(), session_id: sessionId(), distinct_hash: state.hash, surface: surfaceOf() });
  return true;
}

export function flush(nav = typeof navigator !== "undefined" ? navigator : null) {
  if (!state.queue.length || !nav) return false;
  const batch = state.queue.splice(0, QUEUE_MAX);
  try {
    const body = JSON.stringify({ events: batch });
    if (typeof nav.sendBeacon === "function") return nav.sendBeacon(UX_BEACON_URL, new Blob([body], { type: "text/plain" }));
    if (typeof fetch === "function") { fetch(UX_BEACON_URL, { method: "POST", body, headers: { "Content-Type": "text/plain" }, keepalive: true }).catch(() => {}); return true; }
  } catch (e) { /* 앱은 모른다 */ }
  return false;
}

/** 식별 — 로그인하면 해시, 로그아웃·대리 접속이면 anon */
export async function identify(email) {
  if (!email || isImpersonating()) { state.hash = "anon"; return; }
  try { state.hash = (await distinctIdFor(email)) || "anon"; } catch (e) { state.hash = "anon"; }
}

/** 화면 이동 — 이전 화면의 leave(체류 초) + 새 화면 view */
export function route(pathname, now = Date.now()) {
  if (!uxBeaconEnabled()) return;
  const prev = state.route;
  if (prev && state.routeAt) push("leave", prev, { seconds: Math.min(1800, Math.round((now - state.routeAt) / 1000)) });
  push("view", pathname, { prev: prev || null });
  state.route = pathname; state.routeAt = now;
}

function onClick(e) {
  const r = state.route; if (!r || isSensitivePath(r)) return;
  const el = e.target;
  const x = e.clientX || 0, y = e.clientY || 0, now = Date.now();
  const sel = selectorOf(el), text = maskText(el?.innerText || el?.value_placeholder || "");
  push("click", r, { selector: sel, text });
  if (isRage(state.clicks, now, x, y)) push("rage", r, { selector: sel, text });
  state.clicks = [...state.clicks.filter((c) => now - c.t <= RAGE_WINDOW_MS), { t: now, x, y }];
  // 죽은 클릭: 500ms 안에 DOM 변화도 URL 변화도 없으면
  let changed = false;
  const href = typeof window !== "undefined" ? window.location.href : "";
  const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(() => { changed = true; }) : null;
  try { mo?.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true }); } catch (err) { /* noop */ }
  setTimeout(() => {
    try { mo?.disconnect(); } catch (err) { /* noop */ }
    const urlChanged = typeof window !== "undefined" && window.location.href !== href;
    const interactive = ["INPUT", "TEXTAREA", "SELECT"].includes(el?.tagName);
    if (!changed && !urlChanged && !interactive) push("dead", r, { selector: sel, text });
  }, DEAD_WAIT_MS);
}

function onError(message) {
  const r = state.route; if (!r) return;
  push("error", r, { message: String(message || "").slice(0, 200) });
}

/** 한 번만. 키가 없으면 아무것도 하지 않는다. */
export function start(w = typeof window !== "undefined" ? window : null) {
  if (!uxBeaconEnabled() || !w || state.started) return false;
  state.started = true;
  w.addEventListener("click", onClick, { capture: true, passive: true });
  w.addEventListener("error", (e) => onError(e?.message || e?.error?.message));
  w.addEventListener("unhandledrejection", (e) => onError(e?.reason?.message || String(e?.reason || "")));
  const leave = () => { if (state.route && state.routeAt) push("leave", state.route, { seconds: Math.min(1800, Math.round((Date.now() - state.routeAt) / 1000)) }); state.routeAt = Date.now(); flush(); };
  w.addEventListener("pagehide", leave);
  w.document?.addEventListener("visibilitychange", () => { if (w.document.visibilityState === "hidden") leave(); });
  state.timer = setInterval(() => flush(), FLUSH_MS);
  return true;
}
